
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

type Payload = {
  PK: string
  SK: string
  checklist: Array<{ id: string; label: string; done: boolean; createdTime: string }>
  progress: { total: number; done: number }
}

/**
 * Updates a task's checklist + progress atomically.
 * Backend endpoint variant A (recommended): PATCH /aws/tasks/:pk/:sk/checklist
 * Backend endpoint variant B (existing "updates" API): POST /aws/tasks/update with updates[] (shown below).
 */
export const useUpdateTaskChecklist = ({ useBatchUpdates = false } = {}) => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: Payload) => {
      if (!useBatchUpdates) {
        // Variant A: simple PATCH
        const url = `${baseUrl}/aws/tasks/${encodeURIComponent(payload.PK)}/${encodeURIComponent(payload.SK)}/checklist`
        const res = await axios.patch(url, {
          checklist: payload.checklist,
          progress: payload.progress,
        })
        return res.data
      } else {
        // Variant B: your existing "updates" shape
        const res = await axios.post(`${baseUrl}/aws/tasks/update`, {
          PK: payload.PK,
          SK: payload.SK,
          updates: [
            { fieldName: 'checklist', newValue: payload.checklist },
            { fieldName: 'progress', newValue: payload.progress },
          ],
        })
        return res.data
      }
    },

    // Optimistic update
    onMutate: async (payload: Payload) => {
      const key = ['task', payload.PK, payload.SK]
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, (old: any) =>
        old ? { ...old, checklist: payload.checklist, progress: payload.progress } : old
      )
      return { prev, key }
    },

    onError: (_err, _payload, ctx) => {
      if (ctx?.prev && ctx?.key) qc.setQueryData(ctx.key, ctx.prev)
    },

    onSettled: (_data, _err, _payload, ctx) => {
      if (ctx?.key) qc.invalidateQueries({ queryKey: ctx.key })
      // Optionally also invalidate list views:
      // qc.invalidateQueries({ queryKey: ['tasksByOrgId'] })
    },
  })
}
